"use client";

import type { Locale } from "@/lib/i18n/locales";
import { quizProgressCopy, formatQuizProgress } from "@/lib/i18n/quiz-progress-copy";

import { useEffect, useMemo, useState } from "react";

import type { QuizId } from "./registry";
import {
  BUS_QUIZ_CLEAR_EVENT,
  quizStorageKeys,
  BUS_QUIZ_COMPLETION_PREFIX,
  BUS_QUIZ_PROGRESS_PREFIX,
} from "./bus-quiz-storage";

type TopicCompletionSummaryProps = {
  locale?: Locale;
  progressLocales?: Readonly<Record<string, Locale>>;
  quizIds: QuizId[];
  className?: string;
};

type StoredProgress = {
  version: number;
  currentIndex: number;
  totalQuestions?: number;
  answers: Record<number, number>;
  checkedIds: number[];
};

export default function TopicCompletionSummary({
  quizIds,
  locale = "en",
  progressLocales,
  className,
}: TopicCompletionSummaryProps) {
  const [summary, setSummary] = useState<{ answered: number; total: number } | null>(
    null
  );

  const uniqueIds = useMemo(
    () => Array.from(new Set(quizIds)),
    [quizIds]
  );

  useEffect(() => {
    if (uniqueIds.length === 0) return;

    const updateSummary = () => {
      try {
        let answered = 0;
        let total = 0;
        let allComplete = true;

        for (const quizId of uniqueIds) {
          const completionKey = quizStorageKeys(quizId, progressLocales?.[quizId] ?? locale).completion;
          const isComplete = Boolean(window.localStorage.getItem(completionKey));
          if (!isComplete) allComplete = false;

          const progressKey = quizStorageKeys(quizId, progressLocales?.[quizId] ?? locale).progress;
          const raw = window.localStorage.getItem(progressKey);
          if (!raw) continue;
          const parsed = JSON.parse(raw) as StoredProgress | null;
          if (!parsed || typeof parsed !== "object") continue;

          const quizTotal =
            typeof parsed.totalQuestions === "number" && parsed.totalQuestions > 0
              ? parsed.totalQuestions
              : 0;
          if (quizTotal > 0) total += quizTotal;

          const answeredCount = Object.keys(parsed.answers ?? {}).length;
          if (quizTotal > 0) {
            answered += isComplete ? quizTotal : Math.min(answeredCount, quizTotal);
          } else {
            answered += answeredCount;
          }
        }

        if (allComplete) {
          setSummary(null);
          return;
        }

        if (total > 0) {
          setSummary({ answered, total });
          return;
        }

        setSummary(answered > 0 ? { answered, total: 0 } : null);
      } catch (error) {
        console.warn("Failed to read quiz progress state", error);
        setSummary(null);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (
        !event.key.startsWith(BUS_QUIZ_COMPLETION_PREFIX) &&
        !event.key.startsWith(BUS_QUIZ_PROGRESS_PREFIX)
      ) {
        return;
      }
      updateSummary();
    };

    updateSummary();
    window.addEventListener("storage", handleStorage);
    window.addEventListener(BUS_QUIZ_CLEAR_EVENT, updateSummary);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(BUS_QUIZ_CLEAR_EVENT, updateSummary);
    };
  }, [uniqueIds, locale, progressLocales]);

  if (uniqueIds.length === 0) return null;
  if (!summary) return null;

  const classes =
    className ??
    "rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600";

  return (
    <span className={classes}>
      {summary.total > 0
        ? formatQuizProgress(quizProgressCopy(locale).answeredTotal, summary)
        : formatQuizProgress(quizProgressCopy(locale).answered, summary)}
    </span>
  );
}
